import { account, rememberSession, sessionToken } from './auth'
import { apiBase, baseFor, region, setRegion, type Region } from './region'
import { consentPayload } from './consent'
import { syncNow } from './sync'
import { appStorage } from '../platform/web'
import { lang } from '../i18n'

/**
 * Перенос аккаунта между странами хранения (решение заказчика и юриста 19.10.2026).
 *
 * Серверы изолированы, поэтому данные перевозит приложение: оно держит полную локальную
 * копию. Перенос = завести аккаунт заново на сервере новой страны из локальных данных и
 * удалить на старом. Порядок строгий: сперва заливаем на новый, только потом стираем
 * старый — потерять нельзя. Регион устройства переключаем лишь после подтверждения кода:
 * если человек передумал на вводе кода, состояние остаётся прежним.
 *
 * Если старый сервер не удалился (сеть оборвалась), запоминаем и добиваем на следующем
 * запуске — иначе на одну почту осталось бы два живых аккаунта, чего быть не должно.
 */
const PENDING_DELETE = 'moonwake.pending_old_delete'

/** Можно ли сейчас менять страну: сервер держит лимит «раз в 7 дней». */
export async function canChangeRegion(): Promise<{ allowed: boolean; retryAfterMs?: number }> {
  const token = sessionToken()
  if (token === null) return { allowed: false }
  try {
    const r = await fetch(`${apiBase()}/account/region-change`, { headers: { authorization: `Session ${token}` } })
    if (!r.ok) return { allowed: false }
    return (await r.json()) as { allowed: boolean; retryAfterMs?: number }
  } catch {
    return { allowed: false }
  }
}

export interface Migration {
  to: Region
  from: Region
  requestId: string
  newBase: string
  oldBase: string
  oldToken: string | null
}

/** Шаг 1: запросить код у сервера новой страны. Регион ещё не меняем. */
export async function beginMigration(to: Region): Promise<Migration | null> {
  const email = account.value
  if (email === null) return null
  const from = region() ?? 'world'
  const newBase = baseFor(to)
  const r = await fetch(`${newBase}/auth/request`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, lang: lang.value, signup: true, channel: 'web', consents: consentPayload(), region: to, previousRegion: from }),
  })
  if (!r.ok) return null
  const { requestId } = (await r.json()) as { requestId: string }
  return { to, from, requestId, newBase, oldBase: apiBase(), oldToken: sessionToken() }
}

/**
 * Шаг 2: подтвердить код. Заводит сессию на новом сервере, переключает регион, заливает
 * локальные данные и удаляет старый аккаунт. Возвращает код ошибки для экрана или null при успехе.
 */
export async function confirmMigration(m: Migration, code: string): Promise<string | null> {
  const r = await fetch(`${m.newBase}/auth/verify`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ requestId: m.requestId, code: code.trim() }),
  })
  const body = (await r.json().catch(() => ({}))) as { token?: string; error?: string; email?: string }
  if (!r.ok || typeof body.token !== 'string') return body.error ?? 'failed'

  // Код верный: новая сессия, переключаем регион — дальше приложение ходит на новый сервер
  rememberSession(body.token, body.email ?? account.value)
  await setRegion(m.to)
  // Заливаем всё локальное на новый сервер, затем стираем старый аккаунт
  await syncNow(appStorage())
  await удалитьСтарый(m.oldBase, m.oldToken)
  return null
}

async function удалитьСтарый(base: string, token: string | null): Promise<void> {
  if (token === null) return
  try {
    const r = await fetch(`${base}/account`, { method: 'DELETE', headers: { authorization: `Session ${token}` } })
    // 401/410 — аккаунта и так нет: цель достигнута
    if (!r.ok && r.status !== 401 && r.status !== 410) throw new Error(String(r.status))
    try { globalThis.localStorage?.removeItem(PENDING_DELETE) } catch { /* приватный режим */ }
  } catch {
    // Не удалилось сейчас — запомним и добьём на следующем запуске
    try { globalThis.localStorage?.setItem(PENDING_DELETE, JSON.stringify({ base, token })) } catch { /* приватный режим */ }
  }
}

/** Добить удаление старого аккаунта, если прошлый перенос не довёл его до конца. */
export async function retryOldDelete(): Promise<void> {
  let raw: string | null = null
  try {
    raw = globalThis.localStorage?.getItem(PENDING_DELETE) ?? null
  } catch {
    return
  }
  if (raw === null) return
  try {
    const { base, token } = JSON.parse(raw) as { base: string; token: string | null }
    await удалитьСтарый(base, token)
  } catch {
    try { globalThis.localStorage?.removeItem(PENDING_DELETE) } catch { /* приватный режим */ }
  }
}
